import '@lib/utils/react-scan';

import '@lib/style/dynamic-css-vars';
import '@lib/utils/debug';

// Import Inter font with various weights
import '@fontsource/inter/300.css'; // Light
import '@fontsource/inter/400.css'; // Regular
import '@fontsource/inter/500.css'; // Medium
import '@fontsource/inter/600.css'; // Semi-bold
import '@fontsource/inter/700.css'; // Bold

import { ERROR_BOUNDARY_CONSOLE_MARKER } from '@lib/utils/errorBoundaryMarker';
import { errorIsDomDesync } from '@lib/utils/errorIsDomDesync';
import { installNetworkErrorGuard, isNetworkError } from '@lib/utils/networkErrorGuard';
import { countTranslatorFontWrappers, isPageTranslated } from '@lib/utils/pageTranslation';
import { captureConsoleIntegration } from '@sentry/browser';
import * as Sentry from '@sentry/react';
import { billingStore } from '@stores/Billing';
import { initCrossStoreReactions } from '@stores/initCrossStoreReactions';
import '@stores/Tracking';
import { userStore } from '@stores/User';

import ReactDOM from 'react-dom/client';

import { Entrypoint } from '@pages/App/Entry';
import './root.scss';

// Register service worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        console.log('ServiceWorker registration successful with scope: ', registration.scope);

        // Listen for messages from service worker
        navigator.serviceWorker.addEventListener('message', async (event) => {
          if (event.data.type === 'REFRESH_ACCOUNT_DETAILS') {
            // Check local storage for user token
            const userToken = localStorage.getItem('token');
            userStore.setToken(userToken);
            if (userToken) {
              // Fetch user details and update the store
              await userStore.fetch();
              // Also update billing store
              billingStore.refetchCreditsAfterOperation();
            } else {
              // Clear billing and user data when user logs out
              billingStore.data = null;
              userStore.data = null;
              billingStore.clearBillingFromLocalStorage();
            }
          } else if (event.data.type === 'REFRESH_CREDITS') {
            // Load credits from localStorage (updated by the tab that hit the API)
            billingStore.loadBillingFromLocalStorage();
          }
        });
      })
      .catch((error) => {
        console.log('ServiceWorker registration failed: ', error);
      });
  });
}

Sentry.init({
  dsn: process.env.SENTRY_DSN,
  release: process.env.CF_PAGES_COMMIT_SHA,
  environment: process.env.NODE_ENV,
  sampleRate: 1,
  profilesSampleRate: 0.1,
  tracesSampleRate: 0.1,
  // Suppress only INCIDENTAL (unhandled) fetch-level network failures — the
  // background offline rejections installNetworkErrorGuard breadcrumbs — so they
  // don't drown real errors in noise. Gated two ways so it's narrow: the error
  // must be a genuine fetch-level TypeError (isNetworkError, not an app-wide
  // message substring, so `new Error('Failed to fetch config')` still reports),
  // AND it must be unhandled (mechanism.handled === false). Every DELIBERATE
  // Sentry.captureException — error boundaries, VideoCard, and the upload-failure
  // funnel — is handled, so it always reports even when it's a fetch TypeError.
  beforeSend: (event, hint) => {
    const unhandled = event.exception?.values?.some((v) => v.mechanism?.handled === false);
    if (isNetworkError(hint?.originalException) && unhandled) return null;

    // Drop the duplicate that `captureConsoleIntegration` (below) manufactures out of
    // SubtreeErrorBoundary's own `console.error`, so one crash is one issue.
    //
    // Keyed on `logger` + the marker in `extra.arguments`, which is what the integration
    // actually produces: `consoleHandler` scans the arguments for an `instanceof Error`
    // and, finding the one we log, routes to `captureException` — NOT `captureMessage`.
    // So the twin is a full exception event with no `message` to match on, carrying
    // `logger: 'console'`, `mechanism.type: 'console'` and `extra.arguments`. (Matching
    // on `event.message` looks right and silently never fires.)
    //
    // The twin is worth dropping rather than tolerating because it is the WORSE of the
    // two reports while looking like the better one: it groups by the console call site
    // instead of the throw site, and its Additional Data holds a pre-stringified stack
    // Sentry cannot symbolicate. The boundary's own `captureException` carries the same
    // error with a real stacktrace plus the boundary name and context.
    const consoleArgs = event.logger === 'console' ? event.extra?.arguments : undefined;
    if (
      Array.isArray(consoleArgs) &&
      consoleArgs.some(
        (arg) => typeof arg === 'string' && arg.includes(ERROR_BOUNDARY_CONSOLE_MARKER),
      )
    ) {
      return null;
    }

    // Enrich here rather than in the error boundaries, so coverage does not depend on a
    // boundary existing. Plenty of the tree has none — a crash in the Search-side
    // MomentModal, or any unhandled rejection, reaches Sentry without passing through
    // one — and those are exactly the reports that arrive with no context at all.
    // Whether an in-page translator had rewritten the DOM. Two signals, because they
    // fail differently: the class is an undocumented Chrome detail that could vanish,
    // while the `<font>` count measures the actual damage and catches any tool that
    // rewrites text nodes. See `pageTranslation.ts`.
    //
    // Split across tag and extra deliberately. `translated` is a boolean, so it indexes
    // and filters cheaply; the count is unbounded, and an unbounded tag pollutes the
    // per-project tag index for every event in the app. The boolean is what you search
    // on, the count is what you read once you're in.
    event.tags = { ...event.tags, translated: isPageTranslated() };
    event.extra = { ...event.extra, translatorFontWrappers: countTranslatorFontWrappers() };

    // Own bucket for "someone reparented a node React owns". These are invisible in the
    // JS stack (all react-dom internals) so they group badly and read as unactionable;
    // tagging them makes the class searchable and alertable. See `errorIsDomDesync.ts`.
    //
    // Falls back to the serialised exception because `hint.originalException` is not
    // present on every path into `beforeSend`. Relying on the hint alone would tag the
    // deliberate captures and quietly skip some global-handler ones — i.e. lose exactly
    // the reports that have no boundary context to fall back on. `errorIsDomDesync` is
    // duck-typed, so Sentry's `{ type, value }` pair satisfies it directly.
    const serialised = event.exception?.values?.[0];
    const isDomDesync =
      errorIsDomDesync(hint?.originalException) ||
      errorIsDomDesync({ name: serialised?.type, message: serialised?.value });
    if (isDomDesync) {
      event.tags.crashKind = 'dom-desync';
    }

    return event;
  },
  integrations: (defaultIntegrations) => [
    ...defaultIntegrations,
    captureConsoleIntegration({ levels: ['error'] }),
  ],
});

// Neutralize dangling offline-fetch rejections (dev overlay / prod noise) while
// leaving all real errors to surface. Must run before the app mounts.
installNetworkErrorGuard();

// The entire static import graph has evaluated by this point, so store
// reactions that read other stores can bind without racing module init.
initCrossStoreReactions();

const rootElement = document.getElementById('root');

if (rootElement !== null) {
  const root = ReactDOM.createRoot(rootElement, {
    // React 19 logs every boundary-caught error itself — `defaultOnCaughtError` is
    // `console.error(error)` — and does it BEFORE `componentDidCatch` runs. With
    // `captureConsoleIntegration` on, that becomes a Sentry event wrapping the same Error
    // with no boundary context, and because it lands first it becomes `dedupeIntegration`'s
    // `previousEvent` and outranks the boundary's own enriched report.
    //
    // Overriding it drops that event at source. Nothing is lost: every boundary in the app
    // (`SubtreeErrorBoundary`, and `ErrorCatcher` via `Sentry.ErrorBoundary`) reports what it
    // catches, so React's log was pure duplication in production. Kept in development,
    // where an immediate console line is worth more than event hygiene.
    onCaughtError: (error, errorInfo) => {
      if (process.env.NODE_ENV !== 'production') {
        console.error(error, errorInfo);
      }
    },
  });
  root.render(<Entrypoint />);
}
